import { Text } from '@volkswagen-onehub/components-core';
import React, { useEffect, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { getCarlines, oneformBFFNdpsSend } from 'src/bff';
import { CarlineSelect, ConsentQuestion, Email, findCarline, Loading, PantallaNombreApellido } from 'src/components';
import { FormController, removeWhiteSpace, useFaContent, useFeatureAppConfig, useOneFormContext } from 'src/feature-app';
import { getParamsForm } from 'src/feature-app/get-params-form';
import { LayoutWrapper } from 'src/feature-app/Screen';
import { FormDataGroup, FormInfo, OneFormState, Steps } from 'src/types';
import { ConfirmacionProspectsNoQuestions, ThankYouProspects } from '.';
import { formatFields, formatFlags, formatHeaders, formDataBuilder } from '../format-fields';
import { removeStep, resetStepsScreenIndex } from '../helpers';
import { getDescInterestCarline, getMatchData } from '../Leads';

interface ProspectsFormProps {
	inPage?: boolean;
	customSteps?: Steps[];
	updatedFormData?: Partial<FormDataGroup>;
	updatedFormInfo?: Partial<FormInfo>;
}

export function ProspectsVentaForm({ inPage, customSteps, updatedFormData }: ProspectsFormProps) {
	const { initializeForm } = useOneFormContext();
	const store = useStore();
	const { formData, dealersInfo }: OneFormState = store.getState();
	const { fields } = formData;
	const config = useFeatureAppConfig();
	const dispatch = useDispatch();
	const faContent = useFaContent();
	const [loading, setLoading] = useState(true);

	const carlineTitle = fields?.modelo ?? (dealersInfo?.selectedCarline ? `${dealersInfo.selectedCarline.nuevo ? 'Nuevo ' : ''}${dealersInfo.selectedCarline.title}` : 'Volkswagen');

	let index = 0;
	const steps: Steps[] = customSteps
		? customSteps
		: [
				{
					title: (
						<>
							<Text bold>¿Qué modelo </Text> te apetece conducir?
						</>
					),
					fields: (
						<LayoutWrapper>
							<CarlineSelect useCarline={true} />
						</LayoutWrapper>
					),
					screenIndex: index++,
					name: 'Modelo',
					outputs: ['modelo'],
					outputsText: ['modelo'],
				},
				{
					title: (
						<>
							Queremos contártelo todo sobre nuestro próximo {carlineTitle}. Pero antes, nos gustaría saber <Text bold>quién eres</Text>
						</>
					),
					fields: (
						<LayoutWrapper>
							<PantallaNombreApellido />
						</LayoutWrapper>
					),
					screenIndex: index++,
					name: 'NombreApellido',
					outputs: ['name', 'surname', 'secondSurname'],
					outputsText: ['name', 'surname', 'secondSurname'],
				},
				{
					title: (
						<>
							<Text bold>¿A qué email </Text>te enviamos las novedades sobre el {carlineTitle}?
						</>
					),
					fields: (
						<LayoutWrapper>
							<Email paginaConfirmacion={false} required={true} />
						</LayoutWrapper>
					),
					screenIndex: index++,
					name: 'Email',
					outputs: ['email'],
					outputsText: ['email'],
				},
				{
					fields: <ConsentQuestion questionType="lssi" />,
					screenIndex: index++,
					name: 'lssi',
					outputs: ['lssi'],
				},
				{
					fields: <ConsentQuestion questionType="tmk" />,
					screenIndex: index++,
					name: 'tmk',
					outputs: ['tmk'],
				},
				{
					fields: <ConfirmacionProspectsNoQuestions />,
					screenIndex: index++,
					name: 'Confirmacion',
					isLastStep: true,
					hidePreviousStep: true,
				},
		  ];

	const initialFormData: Partial<FormDataGroup> = {
		headers: {
			DPS_TOKEN: 'VW_DDB_FRM_PROSPECT_LAUNCH',
			FORM_OBJECTIVE: 'Prospect',
			SF_CAMPAIGN_ID: '7011n000000JrSNAA0',
			//DESC_CAMPAIGNBLOCK: 'B5. VW Launch Prospects',
			...updatedFormData?.headers,
		},
		flags: {
			PERSON_RGPD_ADAPTED: 'true',
			FLAG_RELACION_MARCA_SIN_IC: 'false',
			...updatedFormData?.flags,
		},
		fields: {
			formName: 'prospect',
			formType: 'otherform',
			pageCategory: 'Prospect',
			formularioFlag: 'prospect',
			...updatedFormData?.fields,
		}, // Analítica
	};

	const sendForm = (): Promise<boolean> => {
		return new Promise<any>(async (resolve, reject) => {
			const { tc, today, url, device } = getParamsForm();

			const { formData, dealersInfo }: OneFormState = store.getState();
			const { fields, headers, flags } = formData;

			const data = {
				...formatHeaders(headers),
				'headers[ORIGIN_DEVICE_TYPE]': device,
				'headers[ORIGIN_TRACKING_CODE]': tc,
				'headers[FORM_URL]': url,
				'headers[FORM_DATE_INSERTION]': today,
				'headers[FORM_NAME]': `vw-oneform-${fields.formName}`,
				'headers[FORM_VERSION]': `vw-oneform-${fields.formName}-${process.env.VERSION}`,

				...formatFlags(flags),

				'fields[DPS_BRAND]': 'Volkswagen',
				'fields[SF_LEAD_RECORDTYPE_ID]': '0121i000000D7NyAAK',
				'fields[SF_LEAD_SALES_INITIAL_ACTION]': 'Prospect',
				'fields[PERSON_NAME]': fields.name,
				'fields[PERSON_SURNAME_1]': fields.surname,
				'fields[PERSON_SURNAME_2]': fields.secondSurname,
				'fields[PERSON_EMAIL]': removeWhiteSpace(fields.email),
				'fields[VEH_INTEREST_ID]': fields.modelo == 'Otro' ? '000000' : dealersInfo?.selectedCarline?.code,
				'fields[VEH_INTEREST_MODEL]': getDescInterestCarline(dealersInfo, fields.modelo),
				...formatFields(fields.formFields),

				'fields[PERSON_RGPD_COM_COMERCIALES]': fields.lssi ? fields.lssi : 'NO INFORMADO',
				'fields[PERSON_RGPD_PERFILADO]': fields.tmk ? fields.tmk : 'NO INFORMADO',
				...(await getMatchData(formData, dealersInfo, faContent)),
			};

			const dataStringified = JSON.stringify(data);
			const dataParsed = JSON.parse(dataStringified);
			const formDataInfo = formDataBuilder(dataParsed);
			const response = await oneformBFFNdpsSend(formDataInfo);

			if (response?.data?.status?.code == 200) {
				dispatch({ type: 'SET_FORM_RESPONSE', payload: response.data });
				resolve(true);
			} else {
				resolve(false);
			}
		});
	};

	const getInitialFormInfo = (steps: Steps[]): Partial<FormInfo> => {
		const firstStep = steps[0];
		const lastStep = steps[steps.length - 1];
		const stepsHistory = [0];
		const numberOfScreens = steps.length;

		return {
			formStarted: true,
			sendFormCallback: sendForm,
			fullScreen: firstStep.fullScreen ? true : false,
			lastStep,
			numberOfScreens,
			notALayer: inPage ? true : false,
			formTheme: inPage ? 'main' : 'inverted',
			formType: inPage ? 'inPage' : 'layer',
			screenIndex: firstStep.screenIndex,
			step: firstStep,
			steps,
			stepsHistory,
			thankYouComponent: <ThankYouProspects />,
		};
	};

	const getFormInfo = async () => {
		let formInitialInfo = getInitialFormInfo(steps);

		if (faContent?.match) {
			await handleCarlines(faContent.match?.carline?.code);
			dispatch({ type: 'SET_OFERTA', payload: faContent.match });
		} else if (faContent?.carlines && faContent?.carlines?.length > 0) {
			await handleCarlines(faContent?.carlines[0]);
			const newSteps = removeStep(steps, 'Modelo');
			const finalSteps = resetStepsScreenIndex(newSteps);
			formInitialInfo = getInitialFormInfo(finalSteps);
		} else {
			await handleCarlines();
		}

		return formInitialInfo;
	};

	const handleCarlines = async (carline?: any) => {
		let carlines = dealersInfo?.carlines;

		// Prevenir multiples requests
		if (!carlines) {
			carlines = await getCarlines();
			dispatch({ type: 'SET_CARLINES', payload: carlines });
		}

		if (carline) {
			dispatch({ type: 'SET_SELECTED_CARLINE', payload: findCarline(carlines, carline) });
		}
	};

	useEffect(() => {
		setLoading(true);
		getFormInfo().then((formInitialInfo) => {
			initializeForm(formInitialInfo, initialFormData);
			setLoading(false);
		});
	}, [carlineTitle]);

	return loading ? <Loading /> : <FormController steps={steps} screenType={inPage ? 'full-screen' : 'layer-screen'} />;
}
